import { resolveApiBaseUrl } from './resolve-api-url'

/**
 * Common shape for API error responses coming from the server.
 */
interface ApiErrorPayload {
  error?: {
    code?: string
    message?: string
    requestId?: string
  }
  [key: string]: unknown
}

/**
 * Callback invoked when any API request receives a 401 response.
 * Set by the auth system to clear auth state reactively (instead of a hard redirect).
 */
let _onUnauthorized: (() => void) | null = null

export function setOnUnauthorized(handler: () => void) {
  _onUnauthorized = handler
}

export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  readonly requestId?: string
  readonly details?: ApiErrorPayload

  constructor(
    message: string,
    status: number,
    options: {
      code?: string
      requestId?: string
      details?: ApiErrorPayload
    } = {}
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = options.code
    this.requestId = options.requestId
    this.details = options.details
  }
}

export interface ApiRequestOptions extends RequestInit {
  json?: unknown
  credentials?: RequestCredentials
}

const apiBaseUrl = resolveApiBaseUrl()

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path
  }

  return `${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Low-level helper for issuing API requests against the ResumeHeaders server.
 * Authentication is handled via httpOnly cookies (credentials: 'include').
 * No Bearer token injection needed.
 */
export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { json, headers, credentials, ...init } = options
  const requestHeaders = new Headers(headers ?? {})

  if (json != null) {
    requestHeaders.set('Content-Type', 'application/json')
    init.body = JSON.stringify(json)
  }

  let response: Response

  try {
    response = await fetch(buildUrl(path), {
      method: init.method ?? (json != null ? 'POST' : 'GET'),
      ...init,
      credentials: credentials ?? 'include',
      headers: new Headers(requestHeaders),
    })
  } catch (error) {
    throw new ApiError(
      error instanceof Error ? error.message : 'Network request failed',
      0
    )
  }

  if (response.ok) {
    const contentType = response.headers.get('content-type') ?? ''
    const isJson = contentType.includes('application/json')
    const payload = isJson
      ? ((await response.json()) as ApiErrorPayload)
      : await response.text()
    return (payload as T) ?? (undefined as T)
  }

  // Handle 401: clear auth state so RequireAuth redirects via the SPA router.
  // Never do a hard window.location redirect — it bypasses the SPA, causes a
  // full reload, and loses in-flight state.
  // Return a never-resolving promise so callers don't see an error or undefined
  // data — the page is about to navigate to sign-in anyway.
  if (response.status === 401) {
    if (_onUnauthorized) {
      _onUnauthorized()
      return new Promise<T>(() => {})
    }
  }

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const payload = isJson
    ? ((await response.json()) as ApiErrorPayload)
    : await response.text()

  const errorPayload = isJson ? (payload as ApiErrorPayload) : undefined
  const message =
    errorPayload?.error?.message ||
    (typeof payload === 'string' && payload.length > 0
      ? payload
      : response.statusText)

  throw new ApiError(message || 'Request failed', response.status, {
    code: errorPayload?.error?.code,
    requestId: errorPayload?.error?.requestId,
    details: errorPayload,
  })
}
